import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Copy, Share2, MessageCircle, Gift } from "lucide-react";
import { api, inr } from "@/lib/api";
import { Skeleton, EmptyState } from "@/components/common";
import { useT } from "@/context/i18n";

export default function Refer() {
  const [ref, setRef] = useState(null);
  const t = useT();
  useEffect(() => { api.get("/referral").then((r) => setRef(r.data)).catch(() => {}); }, []);
  if (!ref) return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-24" /></div>;
  const link = `${window.location.origin}/register?ref=${ref.code}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Referral link copied!"); };
  const wa = () => window.open(`https://wa.me/?text=${encodeURIComponent(`Manage all your loans & EMIs with ZRo Pe. Join with my code ${ref.code}: ${link}`)}`, "_blank");
  const share = async () => { if (navigator.share) { try { await navigator.share({ title: "ZRo Pe", text: `Join ZRo Pe with code ${ref.code}`, url: link }); } catch {} } else copy(); };
  return (
    <div className="space-y-6">
      <div className="relative mx-auto w-full max-w-[900px]">
        <img src="/brand/penguin-refer-earn.png" alt="Refer & Earn — invite friends and earn reward points" className="block h-auto w-full" />
      </div>

      <div className="-mt-2 space-y-3">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3">
          <div>
            <p className="text-xs font-semibold text-[#2563EB]">{t("Your referral earnings")}</p>
            <p className="font-display text-2xl font-bold text-[#0F766E]" data-testid="ref-earnings">{inr(ref.earnings)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Friends invited</p>
            <p className="font-display text-2xl font-bold text-[#0B2E6B]" data-testid="ref-invited">{ref.invites?.length || 0}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-white p-1.5 shadow-sm">
          <input readOnly value={link} className="flex-1 bg-transparent px-3 text-sm text-[#0B2E6B] outline-none" data-testid="ref-link" />
          <button onClick={copy} data-testid="ref-copy" className="rounded-md bg-[#14B8A6] px-3 py-2 text-sm font-semibold text-white"><Copy size={16} /></button>
        </div>
        <div className="flex gap-2">
          <button onClick={wa} data-testid="ref-whatsapp" className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"><MessageCircle size={16} /> {t("WhatsApp")}</button>
          <button onClick={share} data-testid="ref-share" className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"><Share2 size={16} /> {t("Share")}</button>
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-bold text-[#0B132B]">{t("Referral History")}</h2>
        <div className="mt-3">
          {(!ref.invites || ref.invites.length === 0) ? <EmptyState icon={Users} title={t("No referrals yet")} desc={t("Share your link to invite friends. You'll earn points when they make their first payment.")} /> : (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              {ref.invites.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between border-b border-slate-100 px-5 py-3 text-sm last:border-0"><span className="font-medium text-[#0B132B]">{i.name}</span><span className="text-slate-500">{i.status}</span></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
